import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/cn';
import { DialogBackdrop, DialogPopup, DialogPortal, type DialogPopupProps } from '../Dialog';

const dialogContentVariants = cva(
  'border-border-subtle bg-surface-elevated data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 fixed left-1/2 z-50 max-w-[calc(100vw-32px)] border shadow-lg duration-100 outline-none',
  {
    variants: {
      size: {
        sm: 'w-110',
        md: 'w-136',
        lg: 'max-h-[90vh] w-[min(960px,calc(100vw-2rem))] overflow-y-auto',
      },
      /**
       * `default` is centered with padding. `palette` sits at 20% from the top so it does
       * not move when its results change height, has no padding, and clips its sections to
       * the rounded corners. Its width is `w-xl`, whatever `size` says.
       */
      variant: {
        default: 'top-1/2 -translate-1/2 rounded-xl p-6',
        palette: 'top-[20%] w-xl -translate-x-1/2 overflow-hidden rounded-2xl',
      },
    },
    defaultVariants: {
      size: 'sm',
      variant: 'default',
    },
  },
);

export type DialogContentSize = NonNullable<VariantProps<typeof dialogContentVariants>['size']>;
type DialogContentVariant = NonNullable<VariantProps<typeof dialogContentVariants>['variant']>;

export interface DialogContentProps extends DialogPopupProps {
  /**
   * Width preset for the popup.
   * - `sm` (default) — confirmations and short forms (`w-110`).
   * - `md` — denser forms (`w-136`).
   * - `lg` — wide, scrollable content (`w-[min(960px,…)]`, `max-h-[90vh]`).
   * @default 'sm'
   */
  size?: DialogContentSize;
  /** @default 'default' */
  variant?: DialogContentVariant;
}

/**
 * DialogContent bundles the standard dialog chrome — `DialogPortal` +
 * `DialogBackdrop` + the centered, branded `DialogPopup` — into a single
 * styled compound part, mirroring `DropdownMenuContent`.
 *
 * Drop it inside a `Dialog` and fill it with `DialogTitle` / `DialogDescription`
 * and your own content. For layouts that need full control, the raw
 * `DialogPopup` stays available as an escape hatch.
 */
export function DialogContent({
  size = 'sm',
  variant = 'default',
  className,
  children,
  ...props
}: DialogContentProps) {
  return (
    <DialogPortal>
      <DialogBackdrop className="data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0 fixed inset-0 z-50 bg-black/30 duration-100" />
      <DialogPopup
        data-slot="dialog-content"
        className={cn(dialogContentVariants({ size, variant }), className)}
        {...props}
      >
        {children}
      </DialogPopup>
    </DialogPortal>
  );
}
